import Navbar from "@layout/Navbar";
import { useEffect } from "react";
import { Link } from "react-router-dom";

const ORG_STEPS = [
  { icon: "fa-user-plus",   title: "Sign up",              desc: "Create your FirmConnector account in minutes. No credit card required." },
  { icon: "fa-id-card",     title: "Build your team",      desc: "Create employee profiles through a simple automated process." },
  { icon: "fa-unlock",      title: "Give access",          desc: "Let employees manage their own profiles and keep them up to date." },
  { icon: "fa-handshake",   title: "Connect partners",     desc: "Invite vendor and partner firms to access your talent network." },
];

const NETWORK_STEPS = [
  { icon: "fa-envelope-open-text", title: "Request access",  desc: "Ask FirmConnector access from your trusted vendors and partners." },
  { icon: "fa-network-wired",      title: "Search & connect", desc: "Find the skillset you need across your network quickly and easily." },
];

const HIGHLIGHTS = [
  { title: "Unified Network",   desc: "One place for your team and partner talent",      icon: "fa-users",    color: "#2563EB" },
  { title: "Cross-Firm Access", desc: "Share talent across trusted partner firms",        icon: "fa-building", color: "#16A34A" },
  { title: "Smart Matching",    desc: "Find the right skillset fast, no back-and-forth",  icon: "fa-bullseye", color: "#F59E0B" },
  { title: "Built for Speed",   desc: "Streamlined profiles keep everything up to date",  icon: "fa-clock",    color: "#EC4899" },
];

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <Navbar solid />

      {/* ── INTRO STRIP ── */}
      <section style={{ paddingTop: 100, paddingBottom: 80, background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 99, padding: "5px 14px", marginBottom: 20 }}>
                <i className="fa-solid fa-circle-info" style={{ color: "#2563EB", fontSize: 11 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#2563EB", letterSpacing: "0.05em", textTransform: "uppercase" }}>About Us</span>
              </div>
              <h1 style={{ fontSize: "clamp(32px,5vw,52px)", fontWeight: 900, color: "#111827", marginBottom: 20, letterSpacing: "-0.03em", lineHeight: 1.15 }}>
                So how exactly does{" "}
                <span style={{ background: "linear-gradient(90deg,#2563EB,#7C3AED)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  FirmConnector
                </span>{" "}work?
              </h1>
              <p style={{ fontSize: 16, color: "#6B7280", lineHeight: 1.8, marginBottom: 32 }}>
                FirmConnector is a unified platform that connects organizations, talent, vendors, and partners — making workforce management faster, smarter, and more efficient.
              </p>
              <Link to="/contact" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 99, background: "linear-gradient(135deg,#2563EB,#7C3AED)", color: "#fff", fontWeight: 700, fontSize: 14, textDecoration: "none", boxShadow: "0 6px 20px rgba(79,70,229,0.3)" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={e => e.currentTarget.style.transform = ""}>
                <i className="fa-solid fa-envelope" style={{ fontSize: 12 }} /> Get In Touch
              </Link>
            </div>

            {/* Highlights grid */}
            <div className="col-lg-6">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {HIGHLIGHTS.map((s, i) => (
                  <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 16, padding: "24px 20px", textAlign: "center", transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = s.color + "44"; e.currentTarget.style.background = s.color + "08"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.background = "#F8FAFC"; e.currentTarget.style.transform = ""; }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: s.color + "15", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                      <i className={`fa-solid ${s.icon}`} style={{ color: s.color, fontSize: 20 }} />
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", letterSpacing: "-0.01em" }}>{s.title}</div>
                    <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 6, lineHeight: 1.4 }}>{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STEPS ── */}
      <section style={{ padding: "80px 24px", background: "#F8FAFC", borderTop: "1px solid #E5E7EB" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="text-center mb-5">
            <h2 style={{ fontSize: "clamp(24px,4vw,36px)", fontWeight: 800, color: "#111827", marginBottom: 10, letterSpacing: "-0.02em" }}>
              Simple process, powerful results
            </h2>
            <p style={{ fontSize: 16, color: "#6B7280", maxWidth: 480, margin: "0 auto" }}>
              Get your organization up and running in four easy steps.
            </p>
          </div>

          {/* Timeline */}
          <div style={{ position: "relative", maxWidth: 800, margin: "0 auto" }}>
            {/* Vertical line */}
            <div style={{ position: "absolute", left: 23, top: 0, bottom: 0, width: 2, background: "linear-gradient(180deg, #2563EB, #E5E7EB)", borderRadius: 2 }} />

            {ORG_STEPS.map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 20, marginBottom: i < ORG_STEPS.length - 1 ? 28 : 0, position: "relative" }}>
                {/* Step circle */}
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 14px rgba(79,70,229,0.3)", zIndex: 1 }}>
                  <i className={`fa-solid ${step.icon}`} style={{ color: "#fff", fontSize: 16 }} />
                </div>
                {/* Content */}
                <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: "18px 22px", flex: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#BFDBFE"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(79,70,229,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#2563EB", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 99, padding: "2px 9px" }}>Step {i + 1}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{step.title}</span>
                  </div>
                  <p style={{ fontSize: 14, color: "#6B7280", margin: 0, lineHeight: 1.65 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NETWORK SECTION ── */}
      <section style={{ padding: "80px 24px", background: "linear-gradient(150deg,#0A0F1E 0%,#111827 55%,#1A1040 100%)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.015) 1px,transparent 1px)", backgroundSize: "60px 60px", pointerEvents: "none" }} />
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div className="row align-items-center g-5">

            {/* Visual */}
            <div className="col-lg-5 d-none d-lg-block">
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: 28 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 20 }}>Your Network</div>
                {[
                  { name: "TechVentures Inc.",  role: "Partner Firm",  color: "#2563EB", count: "12 staff" },
                  { name: "DesignHub Ltd.",      role: "Vendor",        color: "#16A34A", count: "7 staff"  },
                  { name: "CloudStack Co.",      role: "Partner Firm",  color: "#F59E0B", count: "5 staff"  },
                ].map((firm, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, marginBottom: 8, transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = firm.color + "44"; e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg,#1E3A5F,${firm.color})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className="fa-solid fa-building" style={{ color: "#fff", fontSize: 14 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#E5E7EB" }}>{firm.name}</div>
                      <div style={{ fontSize: 11, color: "#6B7280" }}>{firm.role}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: firm.color, background: firm.color + "18", border: `1px solid ${firm.color}30`, borderRadius: 99, padding: "2px 9px" }}>{firm.count}</span>
                  </div>
                ))}
                <div style={{ marginTop: 16, padding: "12px 14px", background: "rgba(79,70,229,0.12)", border: "1px solid rgba(79,70,229,0.25)", borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
                  <i className="fa-solid fa-magnifying-glass" style={{ color: "#60A5FA", fontSize: 14 }} />
                  <span style={{ fontSize: 13, color: "#A5B4FC", fontWeight: 600 }}>Search across all firms instantly</span>
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="col-lg-7">
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(129,140,248,0.15)", border: "1px solid rgba(129,140,248,0.2)", borderRadius: 99, padding: "5px 14px", marginBottom: 20 }}>
                <i className="fa-solid fa-network-wired" style={{ color: "#60A5FA", fontSize: 11 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#60A5FA", letterSpacing: "0.05em", textTransform: "uppercase" }}>For Your Network</span>
              </div>
              <h2 style={{ fontSize: "clamp(26px,4vw,40px)", fontWeight: 800, color: "#fff", marginBottom: 20, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                As the head of your network
              </h2>

              {NETWORK_STEPS.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 16, marginBottom: 22 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(79,70,229,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={`fa-solid ${step.icon}`} style={{ color: "#60A5FA", fontSize: 17 }} />
                  </div>
                  <div style={{ paddingTop: 4 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#E5E7EB", marginBottom: 6 }}>{step.title}</div>
                    <p style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
                  </div>
                </div>
              ))}

              <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link to="/login" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 99, background: "linear-gradient(135deg,#2563EB,#7C3AED)", color: "#fff", fontWeight: 700, fontSize: 14, textDecoration: "none", boxShadow: "0 6px 20px rgba(79,70,229,0.35)" }}>
                  <i className="fa-solid fa-rocket" style={{ fontSize: 12 }} /> Get Started Free
                </Link>
                <Link to="/contact" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 99, background: "rgba(255,255,255,0.07)", color: "#D1D5DB", border: "1px solid rgba(255,255,255,0.15)", fontWeight: 600, fontSize: 14, textDecoration: "none" }}>
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}